import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AgentSelector } from "@/components/AgentSelector";
import { WorkflowRunSelector } from "@/components/workflows/WorkflowRunSelector";
import { Loader2, Database, Sparkles } from "lucide-react";
import type { Workflow } from "@/types/aiToolkit";

interface GmailInboxActionBarProps {
  selectedCount: number;
  selectedAgentId: string | null;
  onAgentChange: (agentId: string | null) => void;
  selectedWorkflowId: string | null;
  selectedWorkflow: Workflow | null;
  onWorkflowChange: (workflowId: string | null, workflow: Workflow | null) => void;
  onAnalyze: () => void;
  onIndexRaw: () => void;
  isAnalyzing?: boolean;
  isWorkflowRunning?: boolean;
  indexing?: boolean;
  indexProgress?: string | null;
  saveToLibrary: boolean;
  onSaveToLibraryChange: (value: boolean) => void;
  disabled?: boolean;
}

export function GmailInboxActionBar({
  selectedCount,
  selectedAgentId,
  onAgentChange,
  selectedWorkflowId,
  onWorkflowChange,
  selectedWorkflow,
  onAnalyze,
  onIndexRaw,
  isAnalyzing = false,
  isWorkflowRunning = false,
  indexing = false,
  indexProgress,
  saveToLibrary,
  onSaveToLibraryChange,
  disabled = false,
}: GmailInboxActionBarProps) {
  const busy = disabled || isAnalyzing || isWorkflowRunning || indexing;
  const analyzeLabel = selectedWorkflow ? "Run workflow" : "Analyze selected";
  const runDisabled =
    busy || selectedCount === 0 || (!selectedWorkflow && !selectedAgentId);

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 px-3 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <WorkflowRunSelector
          value={selectedWorkflowId}
          integrationSource="gmail"
          onChange={onWorkflowChange}
        />
        <AgentSelector
          value={selectedAgentId}
          onValueChange={onAgentChange}
          compact
          integrationScope="gmail"
        />
        <Button size="sm" onClick={onAnalyze} disabled={runDisabled}>
          {isAnalyzing || isWorkflowRunning ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {analyzeLabel} ({selectedCount})
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onIndexRaw}
          disabled={busy || selectedCount === 0}
        >
          {indexing ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Database className="h-4 w-4 mr-2" />
          )}
          Embed RAG ({selectedCount})
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="gmail-save-library"
          checked={saveToLibrary}
          onCheckedChange={(v) => onSaveToLibraryChange(v === true)}
          disabled={busy}
        />
        <Label htmlFor="gmail-save-library" className="text-sm font-normal cursor-pointer">
          Also save to Library (backup only, not used for chat RAG)
        </Label>
      </div>
      {indexProgress && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          {indexProgress}
        </p>
      )}
    </div>
  );
}
